import { Alert, AlertDescription, AlertIcon, Switch, Text, VStack, Wrap, WrapItem } from '@chakra-ui/react'
import { ReactNode, useEffect, useState } from 'react'

import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { DetailField } from '../../../components/DetailField'
import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { ListInput } from '../../../components/ListInput'
import { LoadingBlock } from '../../../components/Loading'
import { MoneyInput } from '../../../components/MaskedInput'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { StatusBadge } from '../../../components/StatusBadge'
import { TabErrorLabel } from '../../../components/TabErrorLabel'
import { useAppToast } from '../../../helpers'
import { useTabFormErrors } from '../../../helpers/useTabFormErrors'
import { JobRequisitionService } from '../../../services/JobRequisition/JobRequisitionService'
import {
  ContractType,
  EligibleMovementRequest,
  JobRequisitionDetail,
  JobRequisitionInput,
  JobRequisitionSigner
} from '../../../services/JobRequisition/types'
import { IUser, UserService } from '../../../services/User/UserService'
import { LIST_FIELD_LABELS, LIST_FIELDS, LIST_ITEM_MAX_LENGTH, LIST_MAX_ITEMS } from '../JobPositions/constants'
import { CONTRACT_TYPE_OPTIONS, JOB_REQUISITION_STATUS, JOB_REQUISITION_TAB_DEFS, WEEKEND_DAYS } from './constants'
import {
  AreasField,
  DescriptiveNotice,
  IRequisitionSource,
  mpOptionLabel,
  RequisitionSourceFields,
  sourceFromDetail,
  sourceFromMp
} from './JobRequisitionParts'
import { JobRequisitionScheduleEditor } from './JobRequisitionScheduleEditor'

interface IJobRequisitionFormModalProps {
  isOpen: boolean
  onClose: () => void
  /** Called after a successful save, before closing. */
  onSaved: () => void
  /** The RP to edit, waiting for its signature. Empty to open a new one. */
  requisitionId?: number
}

type IJobRequisitionForm = Omit<JobRequisitionInput, 'personnel_movement_request_id'>

const baseForm = (): IJobRequisitionForm => ({
  manager_user_id: null,
  duties: [],
  education: [],
  experience: [],
  skills: [],
  contract_type: '',
  salary: null,
  schedule: [],
  areas: [],
  allow_portfolio_link: false,
  signer_user_id: null
})

const toForm = (detail: JobRequisitionDetail): IJobRequisitionForm => ({
  manager_user_id: detail.manager_user_id,
  duties: detail.duties,
  education: detail.education,
  experience: detail.experience,
  skills: detail.skills,
  contract_type: detail.contract_type,
  salary: Number(detail.salary),
  schedule: detail.schedule,
  areas: detail.areas ?? [],
  allow_portfolio_link: detail.allow_portfolio_link,
  signer_user_id: detail.signer_user_id
})

/** Client-side check mirroring the API rules, keyed like Laravel so the same rendering serves both. */
const validate = (form: IJobRequisitionForm, mpId: number | null): Record<string, string[]> => {
  const errors: Record<string, string[]> = {}
  if (mpId === null) errors.personnel_movement_request_id = ['Selecione a MP.']
  if (!form.manager_user_id) errors.manager_user_id = ['Selecione o gestor imediato.']
  LIST_FIELDS.forEach((field) => {
    if (form[field].length === 0) {
      errors[field] = [`Adicione pelo menos um item em ${LIST_FIELD_LABELS[field].toLowerCase()}.`]
    }
  })
  if (!form.contract_type) errors.contract_type = ['Selecione o regime de contratação.']
  if (!form.salary) errors.salary = ['Informe o salário.']
  if (form.schedule.length === 0) errors.schedule = ['Selecione pelo menos um dia de trabalho.']
  form.schedule.forEach((day, index) => {
    if (!day.start) errors[`schedule.${index}.start`] = ['Informe a entrada.']
    if (!day.end) errors[`schedule.${index}.end`] = ['Informe a saída.']
    if (!day.break) errors[`schedule.${index}.break`] = ['Informe o intervalo.']
    if (WEEKEND_DAYS.includes(day.day) && !day.weekend_mode) {
      errors[`schedule.${index}.weekend_mode`] = ['Informe se é intercalado ou fixo.']
    }
  })
  if (!form.signer_user_id) errors.signer_user_id = ['Selecione quem vai assinar a RP.']
  return errors
}

/**
 * Opens an RP from a finished MP, or edits one still waiting for its signature. Four tabs: what
 * comes from the MP, the description (a copy RH may adjust for this RP only), working hours and
 * salary, and what the site shows plus who signs.
 */
export function JobRequisitionFormModal({ isOpen, onClose, onSaved, requisitionId }: IJobRequisitionFormModalProps) {
  const service = new JobRequisitionService()
  const toast = useAppToast()
  const isEditing = requisitionId !== undefined
  const [form, setForm] = useState<IJobRequisitionForm>(baseForm)
  const {
    errors: fieldErrors,
    setErrors: setFieldErrors,
    tabIndex,
    setTabIndex,
    errorCounts,
    clearErrors,
    listErrors,
    showErrors
  } = useTabFormErrors(JOB_REQUISITION_TAB_DEFS)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [movementRequests, setMovementRequests] = useState<EligibleMovementRequest[]>([])
  const [selectedMpId, setSelectedMpId] = useState<number | null>(null)
  // A new MP waiting for the user to accept losing the edited description.
  const [pendingMpId, setPendingMpId] = useState<number | null>(null)
  const [detail, setDetail] = useState<JobRequisitionDetail | null>(null)
  const [managers, setManagers] = useState<IUser[]>([])
  const [signers, setSigners] = useState<JobRequisitionSigner[]>([])

  useEffect(() => {
    if (!isOpen) return

    setSaving(false)
    setFieldErrors({})
    setTabIndex(0)
    setForm(baseForm())
    setSelectedMpId(null)
    setDetail(null)
    setLoading(true)

    // Managers and signers only fill their selects: a failure warns and leaves the form open.
    Promise.all([
      new UserService()
        .getManagers()
        .then(setManagers)
        .catch((error) => toast.error('Erro ao carregar os gestores', error)),
      service
        .signers()
        .then(setSigners)
        .catch((error) => toast.error('Erro ao carregar os assinantes', error)),
      isEditing
        ? service.show(requisitionId).then((loaded) => {
            setDetail(loaded)
            setForm(toForm(loaded))
          })
        : service.eligibleMovementRequests().then(setMovementRequests)
    ])
      .catch((error) => {
        toast.error(isEditing ? 'Erro ao carregar a RP' : 'Erro ao carregar o formulário da RP', error)
        onClose()
      })
      .finally(() => setLoading(false))
  }, [isOpen, requisitionId])

  const selectedMp = movementRequests.find((mp) => mp.id === selectedMpId) ?? null
  const source: IRequisitionSource | null = detail
    ? sourceFromDetail(detail)
    : selectedMp
      ? sourceFromMp(selectedMp)
      : null
  // A role deactivated (or left incomplete) after the MP cannot open new RPs; the replaced
  // collaborator's role only needs to be complete. The API decides.
  const isRoleUnusable = selectedMp !== null && !selectedMp.job_position?.is_usable

  const updateForm = (patch: Partial<IJobRequisitionForm>) => {
    setForm((prev) => ({ ...prev, ...patch }))
    clearErrors(Object.keys(patch))
  }

  const roleListsOf = (mpId: number | null) => {
    const role = movementRequests.find((item) => item.id === mpId)?.job_position
    return {
      duties: role?.duties ?? [],
      education: role?.education ?? [],
      experience: role?.experience ?? [],
      skills: role?.skills ?? []
    }
  }

  /** Picking the MP fills the description from its role and suggests the requester as manager. */
  const applyMp = (mpId: number | null) => {
    setSelectedMpId(mpId)
    clearErrors(['personnel_movement_request_id'])
    const mp = movementRequests.find((item) => item.id === mpId)
    updateForm({
      ...roleListsOf(mpId),
      manager_user_id: mp?.suggested_manager_id ?? form.manager_user_id
    })
  }

  /** Another MP replaces the description: asked first when RH already edited it. */
  const selectMp = (mpId: number | null) => {
    const original = roleListsOf(selectedMpId)
    const edited = LIST_FIELDS.some((field) => JSON.stringify(form[field]) !== JSON.stringify(original[field]))
    if (selectedMpId !== null && mpId !== selectedMpId && edited) {
      setPendingMpId(mpId)
      return
    }
    applyMp(mpId)
  }

  const handleSave = () => {
    const errors = validate(form, isEditing ? (detail?.personnel_movement_request_id ?? null) : selectedMpId)
    if (Object.keys(errors).length > 0) {
      showErrors(errors)
      return
    }

    setSaving(true)
    setFieldErrors({})
    const request =
      requisitionId !== undefined
        ? service.update(requisitionId, form)
        : service.create({ ...form, personnel_movement_request_id: selectedMpId ?? undefined })
    request
      .then((saved) => {
        toast.success(
          isEditing ? `RP ${saved.number} atualizada` : `RP ${saved.number} enviada para assinatura`,
          isEditing ? undefined : `${saved.signer.name} vai receber o aviso para assinar.`
        )
        onSaved()
        onClose()
      })
      .catch((error) => {
        const { status, data } = error?.response ?? {}
        // The API's handler answers every ValidationException with 400.
        if ((status === 400 || status === 422) && data?.errors) {
          showErrors(data.errors)
          return
        }
        toast.error('Erro ao salvar a RP', error)
      })
      .finally(() => setSaving(false))
  }

  const tabLabel = (index: number) => (
    <TabErrorLabel label={JOB_REQUISITION_TAB_DEFS[index].label} count={errorCounts[index]} />
  )

  const disabled = saving || loading

  const panel = (content: ReactNode) =>
    loading ? (
      <LoadingBlock entity="RP" />
    ) : (
      <VStack align="stretch" spacing={5}>
        {content}
      </VStack>
    )

  const needsMp = (content: ReactNode) =>
    source ? content : <Text variant="caption">Selecione a MP na primeira aba.</Text>

  const mpTab = (
    <>
      {isEditing ? (
        <DetailField label="MP" value={source?.mpLabel} />
      ) : (
        <FormField
          label="Selecione a MP"
          error={fieldErrors.personnel_movement_request_id}
          helperText="MPs concluídas de Aumentar a Equipe ou de Substituição com cargo escolhido."
          isRequired
        >
          <SearchableSelect<number>
            options={movementRequests.map((mp) => ({
              value: mp.id,
              label: `${mpOptionLabel({ id: mp.id, type: mp.type, name: mp.requester_name, created_at: mp.created_at })} · ${mp.job_position?.name ?? ''}`
            }))}
            value={selectedMpId ?? ''}
            onChange={(mpId) => selectMp(mpId === '' ? null : mpId)}
            placeholder="Busque pelo nº, tipo, solicitante ou cargo"
            entity="MP"
            feminine
            isDisabled={disabled}
          />
        </FormField>
      )}
      {selectedMp && (
        <DetailField label="RPs desta MP">
          {selectedMp.requisitions.length === 0 ? (
            <Text variant="caption">Nenhuma RP aberta a partir desta MP ainda.</Text>
          ) : (
            <Wrap>
              {selectedMp.requisitions.map((requisition) => (
                <WrapItem key={requisition.id}>
                  <Text mr={1}>RP {requisition.number}</Text>
                  <StatusBadge status={requisition.status} statuses={JOB_REQUISITION_STATUS} />
                </WrapItem>
              ))}
            </Wrap>
          )}
        </DetailField>
      )}
      {isRoleUnusable && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            O cargo desta MP está desativado ou com o cadastro incompleto. Ajuste o cargo no Cadastro de Cargos para
            abrir a RP.
          </AlertDescription>
        </Alert>
      )}
      {source && <RequisitionSourceFields source={source} />}
      <FormField label="Gestor Imediato" error={fieldErrors.manager_user_id} isRequired>
        <SearchableSelect<number>
          options={managers.map((manager) => ({ value: manager.id, label: manager.name }))}
          value={form.manager_user_id ?? ''}
          onChange={(managerId) => updateForm({ manager_user_id: managerId === '' ? null : managerId })}
          entity="gestor"
          isDisabled={disabled}
        />
      </FormField>
    </>
  )

  const descriptiveTab = needsMp(
    <>
      {source && <DescriptiveNotice source={source} />}
      <Text variant="caption">
        O descritivo vem do Cadastro de Cargos. As alterações feitas aqui valem só para esta RP.
      </Text>
      {LIST_FIELDS.map((field) => (
        <FormField key={field} label={LIST_FIELD_LABELS[field]} error={listErrors(field)} isRequired>
          <ListInput
            value={form[field]}
            onChange={(items) => updateForm({ [field]: items })}
            maxItems={LIST_MAX_ITEMS}
            maxLength={LIST_ITEM_MAX_LENGTH}
            isDisabled={disabled}
          />
        </FormField>
      ))}
    </>
  )

  const scheduleTab = (
    <>
      <FormField label="Regime de Contratação" error={fieldErrors.contract_type} isRequired>
        <FormSelect
          value={form.contract_type}
          onChange={(value) => updateForm({ contract_type: value as ContractType | '' })}
          options={CONTRACT_TYPE_OPTIONS}
          isDisabled={disabled}
        />
      </FormField>
      <FormField label="Salário" error={fieldErrors.salary} isRequired>
        <MoneyInput value={form.salary} onChange={(salary) => updateForm({ salary })} isDisabled={disabled} />
      </FormField>
      <FormField
        label="Dias e horários de trabalho"
        error={fieldErrors.schedule}
        helperText="Marque os dias e informe entrada, saída e intervalo de cada um."
        isRequired
      >
        <JobRequisitionScheduleEditor
          value={form.schedule}
          onChange={(schedule) => updateForm({ schedule })}
          fieldErrors={fieldErrors}
          isDisabled={disabled}
        />
      </FormField>
    </>
  )

  const siteTab = (
    <>
      <AreasField
        value={form.areas}
        onChange={(areas) => updateForm({ areas })}
        error={listErrors('areas')}
        isDisabled={disabled}
      />
      <FormField label="Permitir envio de Portfólio" error={fieldErrors.allow_portfolio_link}>
        <Switch
          isChecked={form.allow_portfolio_link}
          onChange={(e) => updateForm({ allow_portfolio_link: e.target.checked })}
          isDisabled={disabled}
        />
      </FormField>
      <FormField
        label="Assinante"
        error={fieldErrors.signer_user_id}
        helperText="Quem tem a permissão Assinar RP. Recebe o aviso no sino e por e-mail."
        isRequired
      >
        <SearchableSelect<number>
          options={signers.map((signer) => ({ value: signer.id, label: signer.name }))}
          value={form.signer_user_id ?? ''}
          onChange={(signerId) => updateForm({ signer_user_id: signerId === '' ? null : signerId })}
          entity="assinante"
          isDisabled={disabled}
        />
      </FormField>
    </>
  )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title={isEditing ? `Editar RP ${detail?.number ?? ''}` : 'Nova RP'}
        onSubmit={handleSave}
        size="editor"
        confirmAction={isEditing ? 'save' : 'send'}
        confirmLabel={isEditing ? undefined : 'Enviar para assinatura'}
        isLoading={saving}
        isConfirmDisabled={disabled || isRoleUnusable}
        tabIndex={tabIndex}
        onTabChange={setTabIndex}
        // A required field on a hidden tab would make the browser block the submit silently.
        noValidate
        tabs={[
          { label: tabLabel(0), content: panel(mpTab) },
          { label: tabLabel(1), content: panel(descriptiveTab) },
          { label: tabLabel(2), content: panel(scheduleTab) },
          { label: tabLabel(3), content: panel(siteTab) }
        ]}
      />
      <ConfirmDialog
        isOpen={pendingMpId !== null}
        onClose={() => setPendingMpId(null)}
        onConfirm={() => {
          applyMp(pendingMpId)
          setPendingMpId(null)
        }}
        action="save"
        title="Trocar a MP?"
        description="O descritivo que você editou será trocado pelo do cargo desta MP."
        confirmLabel="Trocar MP"
      />
    </>
  )
}
